import { Shield, Info, AlertTriangle } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alert";
import { cn } from "@/lib/utils";

/**
 * SignaturePreview Component Props
 */
export interface SignaturePreviewProps {
  /**
   * The challenge message to be signed
   */
  message: string;
  /**
   * Wallet address that will sign the message
   */
  walletAddress: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to show security information
   * @default true
   */
  showSecurityInfo?: boolean;
}

/**
 * SignaturePreview Component
 *
 * Displays a preview of the message that will be signed by the wallet.
 * Provides security assurance messaging and clear explanation of what signing means.
 *
 * Features:
 * - Clear display of the challenge message
 * - Security information about signing
 * - Wallet address confirmation
 * - Professional, trustworthy design
 *
 * @example
 * ```tsx
 * <SignaturePreview
 *   message="Sign this message to authenticate with BetMeBro..."
 *   walletAddress="7XwM...9kLp"
 * />
 * ```
 */
export function SignaturePreview({
  message,
  walletAddress,
  className,
  showSecurityInfo = true,
}: SignaturePreviewProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Message Preview Card */}
      <div className="rounded-lg border border-border-card bg-bg-page p-6 space-y-4">
        <div className="flex items-start gap-3">
          <Shield
            className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <div className="flex-1 space-y-2">
            <h3 className="text-base font-semibold text-text-dark tracking-tight">
              Message to Sign
            </h3>
            <p className="text-sm text-text-muted leading-relaxed">
              Please review this message carefully before signing.
            </p>
          </div>
        </div>

        {/* Message Content */}
        <div className="mt-4 p-4 rounded-md bg-white border border-border-card">
          <pre className="text-sm text-text-dark font-mono whitespace-pre-wrap break-words">
            {message}
          </pre>
        </div>

        {/* Wallet Address Confirmation */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
          <span>Signing with:</span>
          <code className="px-2 py-1 rounded bg-brand-primary/8 border border-brand-primary/20 text-brand-primary font-mono tabular-nums break-all">
            {walletAddress}
          </code>
        </div>
      </div>

      {/* Security Information — all 3 alerts use brand tokens (info /
          success / warning) instead of raw blue-50/emerald-50/amber-50. */}
      {showSecurityInfo && (
        <div className="space-y-3">
          {/* What Signing Means */}
          <Alert className="border-brand-primary/20 bg-brand-primary/5">
            <Info className="h-4 w-4 text-brand-primary" />
            <AlertTitle className="text-brand-primary text-sm">
              What does signing mean?
            </AlertTitle>
            <AlertDescription className="text-text-secondary text-xs space-y-1">
              <p>
                Signing this message proves you own this wallet without
                revealing your private key. This is a secure, free operation
                that does not involve any blockchain transaction.
              </p>
            </AlertDescription>
          </Alert>

          {/* Security Assurance */}
          <Alert className="border-success/30 bg-success/8">
            <Shield className="h-4 w-4 text-success" />
            <AlertTitle className="text-success text-sm">
              Security Assurance
            </AlertTitle>
            <AlertDescription className="text-text-secondary text-xs space-y-1">
              <ul className="space-y-1 list-disc list-inside">
                <li>This signature cannot move funds from your wallet</li>
                <li>Your private key remains secure in your wallet</li>
                <li>This message is unique and cannot be reused</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Warning */}
          <Alert
            variant="destructive"
            className="border-warning/40 bg-warning/8 text-warning-dark"
          >
            <AlertTriangle className="h-4 w-4 text-warning-dark" />
            <AlertTitle className="text-warning-dark text-sm">
              Never share this signature
            </AlertTitle>
            <AlertDescription className="text-warning-dark/80 text-xs">
              Only sign messages from trusted applications. Never share your
              signature with third parties.
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}

/**
 * Compact variant without detailed security information
 */
export function SignaturePreviewCompact(
  props: Omit<SignaturePreviewProps, "showSecurityInfo">,
) {
  return <SignaturePreview {...props} showSecurityInfo={false} />;
}
